import React, { FC, memo, useMemo } from 'react';
import { DuoPhotoRendererProps, PhotoDuoArrangement, PhotoDuoProps } from './PhotoDuo.types';
import { Box } from '@withjoy/joykit';
import { backgroundImageForUrl } from '@shared/utils/style/backgroundImage';
import { addRendition } from '@shared/utils/photoRendition';
import { AnimatedImageBox } from '../PhotoTrio/PhotoTrio.styles';
import { useGetFilledPhotos } from '../../hooks/useGetFilledPhotos';
import { LazyScreen } from '../LazyScreen/LazyScreen';
import { CroppedImage } from '@shared/components/MultiPhotoSelector/components/CroppedImage/CroppedImage';
import { useGetContainerDimensions } from '../../hooks/useGetContainerDimensions';
import { pxToRem } from '@withjoy/joykit/theme';
import { parseAspectRatio } from '@shared/utils/parseAspectRatio';

const ASPECT_RATIO = {
  primary: '7/5',
  secondary: '5/7',
};
const SCREEN_PROPORTION = {
  primary: '66.66%',
  secondary: '33.33%',
};

const SCREEN_PARTITION = {
  primary: 2 / 3,
  secondary: 1 / 3,
};

const GAP = 4;
const PHOTO_COUNT_TO_SHOW = 2;

const getAspectRatio = (arrangement: PhotoDuoArrangement, index: number) => {
  if (arrangement === 'left') {
    return index === 0 ? ASPECT_RATIO.primary : ASPECT_RATIO.secondary;
  }
  return index === 0 ? ASPECT_RATIO.secondary : ASPECT_RATIO.primary;
};

const getGridTemplateColumns = (arrangement: PhotoDuoArrangement) => {
  return arrangement === 'left' ? `${SCREEN_PROPORTION.primary} ${SCREEN_PROPORTION.secondary}` : `${SCREEN_PROPORTION.secondary} ${SCREEN_PROPORTION.primary}`;
};

const getScreenPartition = (arrangement: PhotoDuoArrangement, index: number) => {
  if (arrangement === 'left') {
    return index === 0 ? SCREEN_PARTITION.primary : SCREEN_PARTITION.secondary;
  }
  return index === 0 ? SCREEN_PARTITION.secondary : SCREEN_PARTITION.primary;
};

const DuoPhotoRenderer: FC<DuoPhotoRendererProps> = props => {
  const { photo, arrangement, index, containerDimensions, firstItemHeight } = props;

  if (photo.cropInfo) {
    const aspectRatio = getAspectRatio(arrangement, index);
    const width = containerDimensions.width * getScreenPartition(arrangement, index);
    return (
      <AnimatedImageBox
        key={photo.id}
        width="100%"
        height="auto"
        aspectRatio={aspectRatio}
      >
        <CroppedImage
          cropInfo={{ ...photo.cropInfo, unit: '%' }}
          imageUrl={addRendition({ url: photo.photoUrl, renditionSize: 'medium' })}
          height={firstItemHeight}
          width={width}
        />
      </AnimatedImageBox>
    );
  }
  return (
    <AnimatedImageBox
      key={photo.id}
      width="100%"
      height="100%"
      aspectRatio={getAspectRatio(arrangement, index)}
      backgroundImage={backgroundImageForUrl(addRendition({ url: photo.photoUrl, renditionSize: 'medium' }))}
      backgroundSize="cover"
      backgroundRepeat="no-repeat"
      backgroundPosition="center"
    />
  );
};

export const PhotoDuo: FC<PhotoDuoProps> = memo(props => {
  const { photos, arrangement, showSkeleton, showPlaceholder } = props;

  const { filledPhotos } = useGetFilledPhotos(photos, PHOTO_COUNT_TO_SHOW);

  const { containerDimensions, containerRef } = useGetContainerDimensions();

  // we will be using the height of first photo for rest of the photos to avoid height mismatch
  const firstItemHeight = useMemo(() => {
    if (photos.length === 0) {
      return 0;
    }
    const firstItemAspectRatio = getAspectRatio(arrangement, 0);
    return (containerDimensions.width * getScreenPartition(arrangement, 0)) / parseAspectRatio(firstItemAspectRatio);
  }, [arrangement, containerDimensions.width, photos.length]);

  if (!showPlaceholder && photos.length === 0) {
    return null;
  }

  return (
    <Box
      display="grid"
      ref={containerRef}
      gridTemplateColumns={{ _: getGridTemplateColumns(arrangement), xs: getGridTemplateColumns(arrangement) }}
      gap={pxToRem(GAP)}
      width="100vw"
      position="relative"
      left="calc(-50vw + 50%)"
      overflow="hidden"
    >
      {filledPhotos.map((photo, index) => (
        <LazyScreen
          key={photo.id}
          src={addRendition({ url: photo.photoUrl, renditionSize: 'medium' })}
          width="100%"
          height="100%"
          aspectRatio={getAspectRatio(arrangement, index)}
          showSkeleton={showSkeleton}
        >
          {() =>
            photo.photoUrl ? (
              <DuoPhotoRenderer
                photo={photo}
                arrangement={arrangement}
                index={index}
                containerDimensions={containerDimensions}
                firstItemHeight={firstItemHeight}
              />
            ) : (
              <Box
                width="100%"
                height="100%"
                maxHeight={firstItemHeight ? pxToRem(firstItemHeight) : 'unset'}
                aspectRatio={getAspectRatio(arrangement, index)}
                backgroundColor="lightgray"
                key={photo.id}
              />
            )
          }
        </LazyScreen>
      ))}
    </Box>
  );
});

PhotoDuo.displayName = 'PhotoDuo';
